import { Chip } from '@ag1/ui';
import { SSGates } from '@portal/state/context/StatsigContext/StatsigFlags';
import { useGateValue } from '@statsig/react-bindings';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SoldOutProductsBannerProps } from './types';

/**
 * This component will create a banner with a message that will show which
 * products are sold out.
 *
 * Shows nothing if the offer was manually marked as sold out for the viewer's
 * locale, or the component is disabled through statsig.
 *
 * - "variants" prop is used for new Atlas driven components
 * - "productsMap" prop is used for sanity driven components
 */
export const SoldOutProductsBanner = ({
  className,
  isOfferSoldOut = false,
  productsInventory,
  variants
}: SoldOutProductsBannerProps) => {
  const { t } = useTranslation('banners');
  const disableSoldOutBanner = useGateValue(SSGates.disableSoldOutBanner);

  const label = useMemo(() => {
    // Don't show sold out variants if the offer is sold out at the locale
    // level.
    //
    // Don't show sold out variants if this component is disabled through
    // statsig.
    if (isOfferSoldOut || disableSoldOutBanner) {
      return null;
    }

    let soldOutCount = 0;
    let soldOutVariantsString = '';

    if (variants && variants.length > 0) {
      soldOutVariantsString = variants.reduce((acc, variant) => {
        if (
          variant.product.tracksInventory &&
          variant.product.totalInventory <= 0 &&
          variant.inventoryPolicy === 'DENY'
        ) {
          soldOutCount += 1;
          const soldOutVariantString = !acc ? variant.product.title : ` & ${variant.product.title}`;

          return `${acc}${soldOutVariantString}`;
        }

        return acc;
      }, '');
    }

    if (!soldOutVariantsString && productsInventory) {
      soldOutVariantsString = Object.values(productsInventory).reduce((acc, product) => {
        if (
          product.tracksInventory &&
          product.totalInventory <= 0 &&
          product.variants.some((variant) => variant.inventoryPolicy === 'DENY')
        ) {
          soldOutCount += 1;
          const soldOutVariantString = !acc ? product.title : ` & ${product.title}`;

          return `${acc}${soldOutVariantString}`;
        }

        return acc;
      }, '');
    }

    if (!soldOutVariantsString) {
      return null;
    }

    return t('offerVariantsSoldOut.soldOut', {
      count: soldOutCount,
      soldOutProducts: soldOutVariantsString
    });
  }, [isOfferSoldOut, disableSoldOutBanner, variants, productsInventory, t]);

  if (!label) {
    return null;
  }

  return <Chip label={label} variant="important" className={className} />;
};
